// Help — the Diátaxis help overlay, live.
//
// Promoted from `sketches/help/HelpOverlay.tsx` (in-app-docs.md §3). The
// static-docs prong of two-pronged support: a centred overlay with EXACTLY
// FOUR TABS (the four Diátaxis quadrants) + a persistent "Ask the assistant"
// footer that bridges to the DO prong. The through-line: each quadrant gets
// its OWN UI language — Tutorial is linear with step indicators and no
// search; How-to is a searchable recipe; Reference is a browse-and-lookup
// table; Explanation is reading-mode prose. The reading pane is one
// exhaustive `switch (doc.quadrant)` — the four reading modes are four types
// (helpContent.ts), so mixing modes is unrepresentable.
//
// Cross-quadrant search groups results by quadrant (via the quadrant badge on
// each row), so a hit never lies about its reading mode. Nav state (tab,
// entry, query) is ephemeral shell state held here — it never touches the
// pure core or the event log.
//
// A11y (Finder precedent): the hard modal contract (focus trap, scrim,
// Esc-to-dismiss, overlay-click, focus return) is Radix's own Dialog
// primitives, used directly rather than the shared `Dialog` wrapper — Help's
// header carries its own search box inline, which the wrapper's fixed
// title+description shape does not have room for.
//
// DO-BRIDGE SHORTCUT: `onAskAssistant` still carries a context string (which
// doc prompted the ask), same shape as the sketch, so the interface is ready
// for a real composer-prefill once one exists. Today's host wiring (see
// AppShellHost) ignores the string and just opens the assistant — there is no
// live "seed the composer" mechanism yet.

import { Trans, useLingui } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { type ReactNode, useState } from "react";
import { Icon } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./Help.css";
import {
    type DocEntry,
    QUADRANTS,
    type Quadrant,
    quadrantMeta,
    searchDocs,
} from "./helpContent.js";

export type HelpProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly docs: readonly DocEntry[];
    readonly initialQuadrant?: Quadrant;
    readonly initialDocId?: string;
    // The DO bridge — hand the current reading context to the concierge.
    readonly onAskAssistant: (context: string) => void;
};

// The reading pane — one exhaustive switch, one UI language per quadrant.
const DocBody = ({ doc }: { readonly doc: DocEntry }): ReactNode => {
    switch (doc.quadrant) {
        case "tutorial":
            // Linear thread, step indicators, generous whitespace, no links.
            return (
                <ol className="bilda-help__steps">
                    {doc.steps.map((step, i) => (
                        <li key={step.title} className="bilda-help__step">
                            <Text
                                as="div"
                                size="xs"
                                weight="medium"
                                tone="faint"
                            >
                                <Trans>
                                    Step {i + 1} of {doc.steps.length}
                                </Trans>
                            </Text>
                            <Text as="div" size="lg" weight="semibold">
                                {step.title}
                            </Text>
                            <Text as="p" size="md" tone="dim">
                                {step.body}
                            </Text>
                        </li>
                    ))}
                </ol>
            );
        case "how_to":
            // A recipe: numbered steps + a result callout + tag chips.
            return (
                <div className="bilda-help__recipe">
                    <ol className="bilda-help__recipe-steps">
                        {doc.recipe.steps.map((s) => (
                            <li key={s}>
                                <Text as="span" size="md">
                                    {s}
                                </Text>
                            </li>
                        ))}
                    </ol>
                    <div className="bilda-help__result">
                        <Text
                            as="span"
                            size="sm"
                            weight="medium"
                            tone="success"
                        >
                            <Trans>Result:</Trans>{" "}
                        </Text>
                        <Text as="span" size="sm" tone="dim">
                            {doc.recipe.result}
                        </Text>
                    </div>
                    <div className="bilda-help__tags">
                        {doc.tags.map((tg) => (
                            <span key={tg} className="bilda-help__tag">
                                <Text as="span" size="xs" tone="faint">
                                    {tg}
                                </Text>
                            </span>
                        ))}
                    </div>
                </div>
            );
        case "reference":
            // Dry rows, browse-not-read.
            return (
                <dl className="bilda-help__ref">
                    {doc.rows.map((row) => (
                        <div key={row.term} className="bilda-help__ref-row">
                            <dt>
                                <Text as="span" size="sm" weight="semibold">
                                    {row.term}
                                </Text>
                            </dt>
                            <dd>
                                <Text as="span" size="sm" tone="dim">
                                    {row.def}
                                </Text>
                            </dd>
                        </div>
                    ))}
                </dl>
            );
        case "explanation":
            // Reading-mode prose — narrow measure, serif, no commands.
            return (
                <div className="bilda-help__prose">
                    {doc.prose.map((p) => (
                        <Text key={p.slice(0, 24)} as="p" size="md" tone="dim">
                            {p}
                        </Text>
                    ))}
                </div>
            );
    }
};

const HelpBody = ({
    docs,
    initialQuadrant = "tutorial",
    initialDocId,
    onAskAssistant,
}: Omit<HelpProps, "open" | "onOpenChange">): ReactNode => {
    const { t, i18n } = useLingui();
    const [quadrant, setQuadrant] = useState<Quadrant>(initialQuadrant);
    const [docId, setDocId] = useState<string | undefined>(initialDocId);
    const [query, setQuery] = useState("");

    const meta = quadrantMeta(quadrant);
    const searching = query.trim() !== "";
    const results = searching ? searchDocs(docs, query) : [];
    const inQuadrant = docs.filter((d) => d.quadrant === quadrant);
    const active =
        docs.find((d) => d.id === docId && d.quadrant === quadrant) ??
        inQuadrant[0];

    // Move the selection when a quadrant tab is picked; reset the open entry.
    const pickQuadrant = (q: Quadrant): void => {
        setQuadrant(q);
        setDocId(undefined);
        setQuery("");
    };

    const openEntry = (entry: DocEntry): void => {
        setQuadrant(entry.quadrant);
        setDocId(entry.id);
        setQuery("");
    };

    // Roving arrow-nav across the vertical tablist.
    const onTablistKey = (e: React.KeyboardEvent): void => {
        const idx = QUADRANTS.findIndex((q) => q.key === quadrant);
        if (e.key === "ArrowDown") {
            e.preventDefault();
            pickQuadrant(QUADRANTS[(idx + 1) % QUADRANTS.length].key);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            pickQuadrant(
                QUADRANTS[(idx - 1 + QUADRANTS.length) % QUADRANTS.length].key,
            );
        }
    };

    return (
        <>
            <header className="bilda-help__header">
                <DialogPrimitive.Title asChild>
                    <Text as="div" size="lg" weight="semibold">
                        <Trans>Help</Trans>
                    </Text>
                </DialogPrimitive.Title>
                {/* Radix requires a Description for the dialog's accessible
                    description; the visible header carries only the title, so
                    this stays screen-reader-only (the Finder precedent). */}
                <VisuallyHidden as="div">
                    <DialogPrimitive.Description>
                        {t`Tutorials, how-to guides, reference and explanation.`}
                    </DialogPrimitive.Description>
                </VisuallyHidden>
                <div className="bilda-help__search">
                    <span
                        className="bilda-help__search-icon"
                        aria-hidden="true"
                    >
                        <Icon name="search" size="sm" />
                    </span>
                    <input
                        type="text"
                        className="bilda-help__search-input"
                        aria-label={t`Search the docs`}
                        placeholder={t`Search all guides…`}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                <DialogPrimitive.Close asChild>
                    <button
                        type="button"
                        className="bilda-help__close"
                        aria-label={t`Close help`}
                    >
                        <Icon name="close" size="sm" />
                    </button>
                </DialogPrimitive.Close>
            </header>

            <div className="bilda-help__body">
                {/* The four quadrant tabs — the ONLY tabs, ever. */}
                <div
                    role="tablist"
                    aria-label={t`Documentation kind`}
                    aria-orientation="vertical"
                    className="bilda-help__tabs"
                    onKeyDown={onTablistKey}
                >
                    {QUADRANTS.map((q) => (
                        <button
                            key={q.key}
                            type="button"
                            role="tab"
                            aria-selected={q.key === quadrant}
                            tabIndex={q.key === quadrant ? 0 : -1}
                            className="bilda-help__tab"
                            onClick={() => pickQuadrant(q.key)}
                        >
                            <span
                                className="bilda-help__tab-icon"
                                aria-hidden="true"
                            >
                                <Icon name={q.icon} size="sm" />
                            </span>
                            <span className="bilda-help__tab-text">
                                <Text as="span" size="sm" weight="medium">
                                    {i18n._(q.label)}
                                </Text>
                                {/* dim, not faint: the selected tab tints its
                                    background (accent 12%), and faint only
                                    clears AA on untinted chrome. */}
                                <Text as="span" size="xs" tone="dim">
                                    {i18n._(q.blurb)}
                                </Text>
                            </span>
                        </button>
                    ))}
                </div>

                {/* Entry list — search results (grouped + badged) or the
                    active quadrant's entries. */}
                <nav
                    className="bilda-help__list"
                    aria-label={
                        searching
                            ? t`Search results`
                            : t`${i18n._(meta.label)} entries`
                    }
                >
                    {(searching ? results : inQuadrant).map((entry) => {
                        const isActive = !searching && entry.id === active?.id;
                        return (
                            <button
                                key={`${entry.quadrant}:${entry.id}`}
                                type="button"
                                className="bilda-help__entry"
                                aria-current={isActive ? "true" : undefined}
                                onClick={() => openEntry(entry)}
                            >
                                {searching ? (
                                    <span className="bilda-help__qtag">
                                        <Text as="span" size="xs" tone="faint">
                                            {i18n._(
                                                quadrantMeta(entry.quadrant)
                                                    .label,
                                            )}
                                        </Text>
                                    </span>
                                ) : null}
                                <Text as="span" size="sm" weight="medium">
                                    {entry.title}
                                </Text>
                                {/* dim, not faint: the active entry
                                    (aria-current) tints its background
                                    (accent 10%), where faint fails AA. */}
                                <Text as="span" size="xs" tone="dim">
                                    {entry.summary}
                                </Text>
                            </button>
                        );
                    })}
                    {searching && results.length === 0 ? (
                        <div className="bilda-help__empty">
                            <Text as="span" size="sm" tone="faint">
                                <Trans>
                                    Nothing matches. Try the assistant instead.
                                </Trans>
                            </Text>
                        </div>
                    ) : null}
                </nav>

                {/* The reading pane — one UI language per quadrant. */}
                <section
                    role="tabpanel"
                    aria-label={active ? active.title : i18n._(meta.label)}
                    className="bilda-help__reader"
                >
                    {active ? (
                        <>
                            <Text as="div" size="xl" weight="semibold">
                                {active.title}
                            </Text>
                            <div className="bilda-help__reader-body">
                                <DocBody doc={active} />
                            </div>
                        </>
                    ) : (
                        <Text as="p" size="sm" tone="faint">
                            <Trans>Pick a guide to read.</Trans>
                        </Text>
                    )}
                </section>
            </div>

            {/* The bridge to the DO prong — always present. */}
            <footer className="bilda-help__footer">
                <Text as="span" size="sm" tone="dim">
                    <Trans>Prefer to just get it done?</Trans>
                </Text>
                <button
                    type="button"
                    className="bilda-help__askbtn"
                    onClick={() =>
                        onAskAssistant(
                            active
                                ? `Help me with: ${active.title}`
                                : "Help me with the app",
                        )
                    }
                >
                    <Icon name="sparkle" size="sm" />
                    <Text as="span" size="sm" weight="medium">
                        <Trans>Ask the assistant</Trans>
                    </Text>
                </button>
            </footer>
        </>
    );
};

export const Help = ({
    open,
    onOpenChange,
    docs,
    initialQuadrant,
    initialDocId,
    onAskAssistant,
}: HelpProps) => (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="bilda-help__scrim" />
            <DialogPrimitive.Content className="bilda-help">
                <HelpBody
                    docs={docs}
                    initialQuadrant={initialQuadrant}
                    initialDocId={initialDocId}
                    onAskAssistant={onAskAssistant}
                />
            </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
);
